import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getVehicles, createVehicle, updateVehicle, deleteVehicle, getBrands, getModels, getSuppliers } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import MoneyInput from '../../components/MoneyInput';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, MagnifyingGlass, Pencil, Trash, Car, GasPump, GearSix } from '@phosphor-icons/react';
import { toast } from 'sonner';

const VehiclesPage = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({
    brand_id: '', model_id: '', year: '', version: '', fuel_type: '',
    transmission: '', color: '', purchase_price: '', selling_price: '', quantity: '1', supplier_id: '', notes: '', photos: []
  });

  const canSeePurchasePrice = currentUser?.role === 'admin' || currentUser?.permissions?.see_purchase_price;

  const loadData = useCallback(async () => {
    try {
      const [vehRes, brandRes, supRes] = await Promise.all([
        getVehicles({ search, brand_id: filterBrand === 'all' ? undefined : filterBrand }),
        getBrands(),
        getSuppliers({})
      ]);
      setVehicles(vehRes.data.vehicles);
      setBrands(brandRes.data);
      setSuppliersList(supRes.data || []);
    } catch {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }, [search, filterBrand]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadModelsForBrand = async (brandId) => {
    if (!brandId) {
      setModels([]);
      return;
    }
    try {
      const res = await getModels({ brand_id: brandId });
      setModels(res.data);
    } catch {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        year: parseInt(formData.year),
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        selling_price: formData.selling_price ? parseFloat(formData.selling_price) : null,
        quantity: formData.quantity ? parseInt(formData.quantity) : 1
      };
      if (selectedVehicle) {
        await updateVehicle(selectedVehicle.id, data);
        toast.success('Vehicle updated');
      } else {
        await createVehicle(data);
        toast.success('Vehicle created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await deleteVehicle(vehicle.id);
      toast.success('Vehicle deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete vehicle');
    }
  };

  const openEdit = async (vehicle) => {
    setSelectedVehicle(vehicle);
    await loadModelsForBrand(vehicle.brand_id);
    setFormData({
      brand_id: vehicle.brand_id || '',
      model_id: vehicle.model_id || '',
      year: vehicle.year?.toString() || '',
      version: vehicle.version || '',
      fuel_type: vehicle.fuel_type || '',
      transmission: vehicle.transmission || '',
      color: vehicle.color || '',
      purchase_price: vehicle.purchase_price?.toString() || '',
      selling_price: vehicle.selling_price?.toString() || '',
      quantity: vehicle.quantity?.toString() || '1',
      supplier_id: vehicle.supplier_id || '',
      notes: vehicle.notes || '',
      photos: vehicle.photos || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedVehicle(null);
    setModels([]);
    setFormData({
      brand_id: '', model_id: '', year: '', version: '', fuel_type: '',
      transmission: '', color: '', purchase_price: '', selling_price: '', quantity: '1', supplier_id: '', notes: '', photos: []
    });
  };

  const handleBrandChange = (brandId) => {
    setFormData({ ...formData, brand_id: brandId, model_id: '' });
    loadModelsForBrand(brandId);
  };

  const fuelTypes = ['Gasoline', 'Hybrid'];
  const transmissions = ['Automatic', 'Manual', 'CVT', 'DCT'];

  return (
    <div data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">360 Stock</h1>
          <p className="text-muted-foreground text-sm">Manage your vehicle inventory</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-vehicle-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('vehicles.addVehicle')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-brutalist pl-10"
            data-testid="vehicle-search"
          />
        </div>
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="w-48 input-brutalist">
            <SelectValue placeholder="All Brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vehicles Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search || filterBrand !== 'all' ? 'No vehicles found' : 'No vehicles yet. Add your first vehicle!'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((vehicle, idx) => (
            <div key={vehicle.id} className={`card-brutalist animate-fade-in stagger-${(idx % 5) + 1}`}>
              {/* Vehicle Image */}
              <div className="aspect-video bg-secondary mb-4 flex items-center justify-center">
                {vehicle.photos?.[0] ? (
                  <img src={vehicle.photos[0]} alt={vehicle.model_name} className="w-full h-full object-cover" />
                ) : (
                  <Car weight="duotone" className="w-16 h-16 text-muted-foreground" />
                )}
              </div>
              
              {/* Vehicle Info */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="overline">{vehicle.brand_name}</p>
                  <h3 className="text-lg font-bold">{vehicle.model_name} {vehicle.version}</h3>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(vehicle)} data-testid={`edit-vehicle-${vehicle.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(vehicle)} className="text-destructive" data-testid={`delete-vehicle-${vehicle.id}`}>
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-2xl font-bold text-primary mb-3">
                {canSeePurchasePrice && vehicle.purchase_price ? `$${vehicle.purchase_price.toLocaleString()}` : vehicle.selling_price ? `${vehicle.selling_price.toLocaleString()} DZD` : '-'}
              </p>
              
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="font-medium">{vehicle.year}</span>
                </span>
                <span className="flex items-center gap-1 font-semibold text-emerald-600">
                  Qty: {vehicle.quantity ?? 1}
                </span>
                {vehicle.fuel_type && (
                  <span className="flex items-center gap-1">
                    <GasPump className="w-4 h-4" />
                    {vehicle.fuel_type}
                  </span>
                )}
                {vehicle.transmission && (
                  <span className="flex items-center gap-1">
                    <GearSix className="w-4 h-4" />
                    {vehicle.transmission}
                  </span>
                )}
                {vehicle.color && (
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border border-border" style={{ backgroundColor: vehicle.color.toLowerCase() }}></span>
                    {vehicle.color}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedVehicle ? t('vehicles.editVehicle') : t('vehicles.addVehicle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('vehicles.brand')} *</Label>
                <Select value={formData.brand_id} onValueChange={handleBrandChange}>
                  <SelectTrigger className="input-brutalist" data-testid="vehicle-brand-select">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('vehicles.model')} *</Label>
                <Select value={formData.model_id} onValueChange={(v) => setFormData({ ...formData, model_id: v })}>
                  <SelectTrigger className="input-brutalist" data-testid="vehicle-model-select">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((model) => (
                      <SelectItem key={model.id} value={model.id}>{model.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('vehicles.year')} *</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="2024"
                  required
                  className="input-brutalist"
                  data-testid="vehicle-year-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('vehicles.version')}</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="e.g., XLE, Sport"
                  className="input-brutalist"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('vehicles.fuelType')}</Label>
                <Select value={formData.fuel_type} onValueChange={(v) => setFormData({ ...formData, fuel_type: v })}>
                  <SelectTrigger className="input-brutalist">
                    <SelectValue placeholder="Select fuel type" />
                  </SelectTrigger>
                  <SelectContent>
                    {fuelTypes.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('vehicles.transmission')}</Label>
                <Select value={formData.transmission} onValueChange={(v) => setFormData({ ...formData, transmission: v })}>
                  <SelectTrigger className="input-brutalist">
                    <SelectValue placeholder="Select transmission" />
                  </SelectTrigger>
                  <SelectContent>
                    {transmissions.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('vehicles.color')}</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="e.g., White, Black"
                  className="input-brutalist"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Selling Price (DZD)</Label>
                <Input
                  type="number"
                  value={formData.selling_price}
                  onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                  placeholder="4500000"
                  className="input-brutalist"
                  data-testid="vehicle-selling-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantity in Stock</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="1"
                  min="0"
                  className="input-brutalist"
                  data-testid="vehicle-quantity"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {canSeePurchasePrice && (
              <div className="space-y-2">
                <Label>Purchase Price ($)</Label>
                <MoneyInput
                  value={formData.purchase_price}
                  onChange={(v) => setFormData({ ...formData, purchase_price: v })}
                  placeholder="25 000"
                  className="input-brutalist"
                  data-testid="vehicle-price-input"
                />
              </div>
              )}
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}>
                  <SelectTrigger className="input-brutalist" data-testid="vehicle-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliersList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="input-brutalist"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="vehicle-submit-btn">
                {t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesPage;
